/** Custom report builder dialog: compose multi-section workbooks with column & filter choices. */
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { errorMessage } from "@/api/problem";
import {
  type CustomReport,
  type CustomSectionSpec,
  type DatasetDefinition,
  useSaveCustomReport,
} from "@/features/reports/queries";
import { FormDialog } from "@/shared/components/form-dialog";
import { Button } from "@/shared/ui/button";
import { Checkbox, Field, Input, Select } from "@/shared/ui/form";

const ROLE_OPTIONS = [
  "",
  "supervisor",
  "planner",
  "asset_manager",
  "storekeeper",
  "procurement",
  "finance",
  "viewer",
] as const;

function defaultSection(dataset: DatasetDefinition): CustomSectionSpec {
  return {
    dataset: dataset.key,
    title: dataset.name,
    columns: dataset.columns.filter((c) => c.default).map((c) => c.key),
    filters: {},
  };
}

/** Dialog to create or edit a multi-section custom report template. */
export function CustomReportDialog({
  report,
  datasets,
  onClose,
}: {
  report?: CustomReport;
  datasets: DatasetDefinition[];
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const save = useSaveCustomReport();
  const [name, setName] = useState(report?.name ?? "");
  const [description, setDescription] = useState(report?.description ?? "");
  const [targetRole, setTargetRole] = useState(report?.target_role ?? "");
  const [defaultDays, setDefaultDays] = useState(String(report?.default_days ?? 30));
  const [isShared, setIsShared] = useState(report?.is_shared ?? true);
  const [sections, setSections] = useState<CustomSectionSpec[]>(() =>
    report?.sections.length
      ? report.sections.map((s) => ({
          dataset: s.dataset,
          title: s.title ?? "",
          columns: [...(s.columns ?? [])],
          filters: { ...(s.filters ?? {}) },
        }))
      : datasets[0]
        ? [defaultSection(datasets[0])]
        : [],
  );
  const [error, setError] = useState<string>();

  const updateSection = (idx: number, patch: Partial<CustomSectionSpec>): void => {
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const moveSection = (idx: number, dir: -1 | 1): void => {
    setSections((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      const [item] = next.splice(idx, 1);
      if (item) next.splice(target, 0, item);
      return next;
    });
  };

  const toggleColumn = (idx: number, colKey: string, checked: boolean): void => {
    const current = sections[idx];
    if (!current) return;
    const cols = current.columns ?? [];
    const nextCols = checked ? [...new Set([...cols, colKey])] : cols.filter((c) => c !== colKey);
    updateSection(idx, { columns: nextCols });
  };

  const submit = async (): Promise<void> => {
    setError(undefined);
    if (!name.trim()) {
      setError(t("app.required"));
      return;
    }
    if (sections.length === 0 || sections.some((s) => (s.columns ?? []).length === 0)) {
      setError(t("reports.customColumnsRequired"));
      return;
    }
    try {
      await save.mutateAsync({
        id: report?.id,
        body: {
          name: name.trim(),
          description: description.trim(),
          target_role: targetRole,
          default_days: Math.max(1, Math.min(365, Number(defaultDays) || 30)),
          is_shared: isShared,
          sections,
        },
      });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t(report ? "reports.editCustomReport" : "reports.newCustomReport")}
      description={t("reports.customBuilderHint")}
      onSubmit={submit}
      submitting={save.isPending}
      error={error}
      wide
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="custom-report-name" label={t("app.name")}>
          <Input
            id="custom-report-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={150}
            required
          />
        </Field>
        <Field id="custom-report-role" label={t("reports.targetRole")}>
          <Select
            id="custom-report-role"
            value={targetRole}
            onChange={(e) => setTargetRole(e.target.value)}
          >
            {ROLE_OPTIONS.map((role) => (
              <option key={role} value={role}>
                {role ? t(`reports.roles.${role}`, { defaultValue: role }) : t("reports.allRoles")}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <Field id="custom-report-desc" label={t("app.description")}>
            <Input
              id="custom-report-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={400}
            />
          </Field>
        </div>
        <Field id="custom-report-days" label={t("reports.days")}>
          <Input
            id="custom-report-days"
            type="number"
            min={1}
            max={365}
            value={defaultDays}
            onChange={(e) => setDefaultDays(e.target.value)}
          />
        </Field>
      </div>
      <Checkbox
        id="custom-report-shared"
        label={t("reports.shareWithTenant")}
        checked={isShared}
        onChange={(e) => setIsShared(e.target.checked)}
      />

      <div className="space-y-3 border-t pt-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold">{t("reports.sections")}</h4>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={datasets.length === 0 || sections.length >= 10}
            onClick={() => {
              const first = datasets[0];
              if (first) setSections((prev) => [...prev, defaultSection(first)]);
            }}
          >
            <Plus className="me-1 size-3.5" aria-hidden />
            {t("reports.addSection")}
          </Button>
        </div>

        <div className="max-h-96 space-y-3 overflow-y-auto pe-1">
          {sections.map((section, idx) => {
            const ds = datasets.find((d) => d.key === section.dataset) ?? datasets[0];
            return (
              <div key={idx} className="space-y-3 rounded-md border bg-muted/20 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t("reports.sectionNumber", { n: idx + 1 })}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-7"
                      disabled={idx === 0}
                      aria-label={t("reports.moveUp")}
                      onClick={() => moveSection(idx, -1)}
                    >
                      <ArrowUp className="size-3.5" aria-hidden />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-7"
                      disabled={idx === sections.length - 1}
                      aria-label={t("reports.moveDown")}
                      onClick={() => moveSection(idx, 1)}
                    >
                      <ArrowDown className="size-3.5" aria-hidden />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-7 text-destructive"
                      disabled={sections.length <= 1}
                      aria-label={t("app.remove")}
                      onClick={() => setSections((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      <Trash2 className="size-3.5" aria-hidden />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field id={`sec-ds-${String(idx)}`} label={t("reports.dataset")}>
                    <Select
                      id={`sec-ds-${String(idx)}`}
                      value={section.dataset}
                      onChange={(e) => {
                        const nextDs = datasets.find((d) => d.key === e.target.value);
                        if (nextDs) {
                          updateSection(idx, defaultSection(nextDs));
                        }
                      }}
                    >
                      {datasets.map((d) => (
                        <option key={d.key} value={d.key}>
                          {d.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field id={`sec-title-${String(idx)}`} label={t("reports.sheetTitle")}>
                    <Input
                      id={`sec-title-${String(idx)}`}
                      value={section.title ?? ""}
                      onChange={(e) => updateSection(idx, { title: e.target.value })}
                      maxLength={31}
                    />
                  </Field>
                </div>

                {ds && ds.filters.length > 0 ? (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {ds.filters.map((f) => (
                      <Field key={f.key} id={`sec-${String(idx)}-filter-${f.key}`} label={f.label}>
                        <Select
                          id={`sec-${String(idx)}-filter-${f.key}`}
                          value={section.filters?.[f.key] ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            const nextFilters = Object.fromEntries(
                              Object.entries(section.filters ?? {}).filter(([k]) => k !== f.key),
                            );
                            if (val) nextFilters[f.key] = val;
                            updateSection(idx, { filters: nextFilters });
                          }}
                        >
                          <option value="">{t("app.all")}</option>
                          {f.options.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </Select>
                      </Field>
                    ))}
                  </div>
                ) : null}

                {ds ? (
                  <div>
                    <p className="mb-1.5 text-xs font-medium text-muted-foreground">
                      {t("reports.columns")}
                    </p>
                    <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                      {ds.columns.map((col) => (
                        <Checkbox
                          key={col.key}
                          id={`sec-${String(idx)}-col-${col.key}`}
                          label={col.label}
                          checked={(section.columns ?? []).includes(col.key)}
                          onChange={(e) => toggleColumn(idx, col.key, e.target.checked)}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </FormDialog>
  );
}
